import type { ZoneBoardLegendRow } from '../lib/zone-board';
import { ZoneCell } from './ZoneIndicators';

/**
 * The per-zone legend every read-only ops board carries (admin-portal-ui-standards.md §
 * "Read-only ops boards are zone-agnostic"). One row per zone the platform has — including a
 * zone that did not respond or that this console was built without — so a missing zone is a
 * visible row with a status, never an absence.
 *
 * Always rendered, whatever the zone filter shows: the counts are over the unfiltered rows.
 */
export function ZoneBoardLegend({
  rows,
  columns,
  testId,
  ariaLabel,
  loading = false,
}: {
  rows: ZoneBoardLegendRow[];
  /** The count columns, in order — `key` indexes `ZoneBoardLegendRow.counts`. */
  columns: ReadonlyArray<{ key: string; label: string }>;
  testId: string;
  ariaLabel: string;
  /** First load in flight — counts are not yet known, so they read "—" rather than 0. */
  loading?: boolean;
}) {
  return (
    <table className="zone-board-legend" data-testid={testId} aria-label={ariaLabel}>
      <thead>
        <tr>
          <th scope="col">Zone</th>
          <th scope="col">Status</th>
          {columns.map((c) => <th scope="col" key={c.key}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          // An unconfigured zone is never "loading" — the console cannot reach it at all.
          const state = loading && r.state !== 'not-configured' ? 'loading' : r.state;
          return (
          <tr key={r.code} data-zone-row={r.code} data-zone-state={state}>
            <th scope="row"><ZoneCell zone={r.code} /></th>
            <td className={`zone-board-status zone-board-status-${state}`}>
              {state === 'loading' ? 'Loading…' : r.status}
            </td>
            {columns.map((c) => (
              <td key={c.key} data-col={c.key}>
                {loading || r.state === 'failed' || r.state === 'not-configured' ? '—' : (r.counts[c.key] ?? 0)}
              </td>
            ))}
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}
